<template>
  <div class="rs-page">
    <div class="container-fluid py-5">
      <div class="rs-inner">
        <!-- Top: title + large search bar -->
        <header class="rs-header">
          <div class="rs-head-text">
            <div class="rs-kicker">RESULTS</div>
            <h1 class="rs-title">Results Center</h1>
            <p class="rs-subtitle">
              Open the full results page directly using a taskId, or quickly navigate via the example cards below.
            </p>
          </div>

          <!-- Google-style large search bar (no emoji) -->
          <div class="rs-search-shell">
            <div class="rs-search-bar">
              <span class="rs-search-icon"></span>
              <input
                v-model="taskIdInput"
                type="text"
                class="rs-search-input"
                placeholder="Enter a taskId, e.g., task_20251208_xxx"
                @keyup.enter="goQuery"
              />
              <button
                class="btn btn-primary rs-search-btn"
                @click="goQuery"
                :disabled="!taskIdInputTrimmed"
              >
                Open Results
              </button>
            </div>
            <div class="rs-search-hint">
              You can copy a taskId from external records or logs and paste it here.
              Press Enter or click “Open Results” to navigate.
            </div>
          </div>
        </header>

        <!-- Spacer -->
        <div class="rs-spacer"></div>

        <!-- Recommended examples: admin-configured demo tasks -->
        <section class="rs-section">
          <div class="rs-block">
            <div class="rs-block-head d-flex justify-content-between align-items-end flex-wrap gap-2">
              <div>
                <div class="rs-block-title">Example Results</div>
                <div class="rs-block-subtitle">
                  Administrator-recommended completed tasks for inspecting logs, progress tables, and output images.
                </div>
              </div>

              <button
                class="btn btn-sm btn-outline-secondary"
                @click="shuffleExamples"
                :disabled="loading || !exampleTasks.length"
                title="Randomly display 4 examples"
              >
                Refresh
              </button>
            </div>

            <!-- Info line -->
            <div class="rs-section-caption">
              <template v-if="loading">
                Loading example tasks…
              </template>
              <template v-else>
                Currently showing {{ visibleExamples.length }} example tasks out of {{ exampleTasks.length }} configurations.
                Click any card to jump directly to the corresponding results page.
              </template>
            </div>

            <div class="rs-grid" v-if="!loading && visibleExamples.length">
              <article
                v-for="(ex, idx) in visibleExamples"
                :key="ex.id || ex.taskId"
                :class="['rs-card', { 'is-lead': idx === 0 }]"
                role="button"
                tabindex="0"
                @click="openExample(ex)"
                @keyup.enter="openExample(ex)"
              >
                <div class="rs-card-kicker">
                  <span class="rs-card-tag">{{ ex.domain || "Workflow" }}</span>
                  <span class="rs-card-dot">•</span>
                  <span class="rs-card-meta">taskId</span>
                  <span class="rs-card-code">{{ ex.taskId }}</span>
                </div>

                <div class="rs-card-title">
                  {{ ex.title }}
                </div>

                <div class="rs-card-desc">
                  {{ ex.desc }}
                </div>

                <div class="rs-card-footer">
                  <span class="rs-card-soft">
                    {{ ex.summary }}
                  </span>
                  <span class="rs-card-pill" v-if="ex.badge">
                    {{ ex.badge }}
                  </span>
                </div>
              </article>
            </div>

            <div v-if="!loading && !visibleExamples.length" class="rs-empty">
              {{ errorMsg || "No example configurations available. Please contact the administrator to add recommended tasks." }}
            </div>
          </div>
        </section>

        <div class="py-3"></div>
      </div>
    </div>
  </div>
</template>

<script>
import API_BASE_URL from '@/api'
import axios from 'axios'

export default {
  name: "ResultsPage",
  data() {
    return {
      taskIdInput: "",
      exampleTasks: [],       // loaded from backend
      visibleExamples: [],
      loading: false,
      errorMsg: "",
    };
  },
  computed: {
    taskIdInputTrimmed() {
      return (this.taskIdInput || "").trim();
    },
  },
  async mounted() {
    await this.loadExampleTasks();
    this.shuffleExamples();
  },
  methods: {
    // Load example tasks from backend
    async loadExampleTasks() {
      this.loading = true;
      this.errorMsg = "";
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/common/example_tasks/`,
        );
        const items = Array.isArray(res.data?.items) ? res.data.items : [];

        // Compatibility mapping for different backend field styles
        this.exampleTasks = items.map((t) => ({
          id: t.id ?? t.pk ?? t.taskId ?? t.task_id,
          taskId: t.taskId ?? t.task_id ?? "",
          title: t.title ?? "",
          desc: t.desc ?? t.description ?? t["desc"] ?? "",
          domain: t.domain ?? "",
          summary: t.summary ?? "",
          badge: t.badge ?? "",
        })).filter((t) => t.taskId && t.title);

      } catch (e) {
        console.error("loadExampleTasks failed:", e);
        this.exampleTasks = [];
        this.visibleExamples = [];
        this.errorMsg = "Failed to load example tasks. Please check the backend API / CORS / network.";
      } finally {
        this.loading = false;
      }
    },

    // Navigate to QueryPage.vue using the input taskId
    goQuery() {
      const id = this.taskIdInputTrimmed;
      if (!id) return;
      this.$router.push({
        name: "QueryTask",
        params: { taskId: id },
      });
    },

    openExample(ex) {
      if (!ex || !ex.taskId) return;
      this.$router.push({
        name: "QueryTask",
        params: { taskId: ex.taskId },
      });
    },

    // Randomly display 4 examples
    shuffleExamples() {
      if (!this.exampleTasks || !this.exampleTasks.length) {
        this.visibleExamples = [];
        return;
      }
      const pool = [...this.exampleTasks];
      const shuffled = pool.sort(() => Math.random() - 0.5);
      this.visibleExamples = shuffled.slice(0, 4);
    },
  },
};
</script>

<style scoped>
.rs-page {
  min-height: 100vh;
  color: rgba(15, 23, 42, 0.92);

  /* Same fog-style background as HomePage */
  background:
    radial-gradient(900px 420px at 18% 0%, rgba(13,110,253,0.10), transparent 60%),
    radial-gradient(820px 420px at 82% 8%, rgba(102,16,242,0.08), transparent 58%),
    linear-gradient(180deg, rgba(255,255,255,0.62) 0%, rgba(255,255,255,0.34) 100%);

  /* Key: clip background with app-container rounding */
  border-radius: 18px;
  overflow: hidden;
  background-clip: padding-box;
}

/* Control overall width */
.rs-inner {
  max-width: 1200px;
  margin: 0 auto;
}

/* Top: title + large search bar */
.rs-header {
  margin-bottom: 48px;
  text-align: center;
}

.rs-head-text {
  margin-bottom: 20px;
}

.rs-kicker {
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
  font-weight: 900;
}

.rs-title {
  font-size: 34px;
  font-weight: 950;
  letter-spacing: -0.03em;
  margin-bottom: 6px;
}

.rs-subtitle {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.68);
}

/* Google-style search bar (no emoji) */
.rs-search-shell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.rs-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 640px;
  background: #ffffff;
  border-radius: 999px;
  padding: 8px 10px 8px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
}

/* Reserved icon area (no symbol, spacing only) */
.rs-search-icon {
  width: 18px;
  height: 18px;
}

.rs-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  background: transparent;
  padding: 6px 4px;
}

.rs-search-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.rs-search-btn {
  border-radius: 999px;
  font-size: 14px;
  padding: 6px 18px;
  white-space: nowrap;
}

.rs-search-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 4px;
}

/* Push the examples section down slightly */
.rs-spacer {
  height: 26px;
}

/* Content block: remove white background */
.rs-section {
  margin-top: 24px;
  margin-bottom: 40px;
}

.rs-block {
  border-top: 3px solid rgba(0, 0, 0, 0.85);
  padding-top: 16px;
  padding-bottom: 12px;
  background: transparent;
}

.rs-block-head {
  margin-bottom: 10px;
}

.rs-block-title {
  font-size: 18px;
  font-weight: 950;
  letter-spacing: -0.01em;
}

.rs-block-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

/* Info line */
.rs-section-caption {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 10px;
}

/* Cards */
.rs-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 4px;
}

.rs-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 12px 12px;
  background: #fff;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

/* Lead card */
.rs-card.is-lead {
  grid-column: span 2;
  padding: 16px 16px;
  background: linear-gradient(135deg, #fdfcfb 0%, #f5f7fa 100%);
}

.rs-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 0, 0, 0.18);
}

.rs-card-kicker {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.62);
}

.rs-card-tag {
  font-weight: 950;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.rs-card-dot {
  opacity: 0.6;
}

.rs-card-meta {
  font-weight: 800;
}

.rs-card-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    monospace;
  color: rgba(0, 0, 0, 0.55);
}

.rs-card-title {
  margin-top: 8px;
  font-size: 15px;
  font-weight: 950;
  line-height: 1.25;
}

.rs-card-desc {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.6;
  min-height: 52px;
}

.rs-card-footer {
  margin-top: 10px;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.58);
}

.rs-card-soft {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}

.rs-card-pill {
  border: 1px solid rgba(0, 0, 0, 0.14);
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
  font-weight: 900;
}

.rs-empty {
  margin-top: 10px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}

/* Responsive */
@media (max-width: 768px) {
  .rs-search-bar {
    max-width: 100%;
    flex-wrap: wrap;
    padding-right: 12px;
  }
  .rs-search-btn {
    width: 100%;
    justify-content: center;
  }
  .rs-grid {
    grid-template-columns: 1fr;
  }
  .rs-card.is-lead {
    grid-column: auto;
  }
}

@media (max-width: 1200px) {
  .rs-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

